import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { matchesWorkerSearch } from '../configuration.utils';
import {
    buildOrderedConfigurationSidebarItems,
    ConfigurationSidebarItem,
    getAutomationWorkerOrderNumber,
    getWorkflowStatusOrderNumber,
} from '../configuration-worker-execution-order';
import {
    getWorkflowStatusDescription,
    getWorkflowStatusIcon,
    getWorkflowStatusLabel,
} from '../configuration-worker-metadata';
import * as S from './configuration-sidebar.styles';

interface IConfigurationSidebarProps {
    store: WorkerStore;
}

function matchesSidebarItem(searchQuery: string, item: ConfigurationSidebarItem): boolean {
    if (item.kind === 'automation') {
        const { worker } = item;
        return matchesWorkerSearch(
            searchQuery,
            worker.name,
            worker.desc,
            worker.scopeLabel,
            worker.key,
            getAutomationWorkerOrderNumber(worker.id) ?? '',
        );
    }

    const label = getWorkflowStatusLabel(item.status.workflowStatus);
    const description = getWorkflowStatusDescription(item.status.workflowStatus);

    return matchesWorkerSearch(
        searchQuery,
        label,
        description,
        item.status.workflowStatus,
        getWorkflowStatusOrderNumber(item.status.workflowStatus) ?? '',
    );
}

export const ConfigurationSidebar: React.FC<IConfigurationSidebarProps> = observer(({ store }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const sidebarItems = useMemo(() => {
        const orderedItems = buildOrderedConfigurationSidebarItems(
            store.automationWorkers,
            store.configurationOptions,
        );

        return orderedItems.filter(item => matchesSidebarItem(searchQuery, item));
    }, [searchQuery, store.automationWorkers, store.configurationOptions]);

    return (
        <S.Sidebar>
            <S.SidebarHeader>
                <S.SidebarTitle>Workers</S.SidebarTitle>
                <S.SearchField>
                    <Icon path={mdiMagnify} size={0.7} color="#8e9293" aria-hidden={true} />
                    <S.SearchInput
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search workers"
                        aria-label="Search workers"
                    />
                </S.SearchField>
            </S.SidebarHeader>

            <S.WorkerList>
                {sidebarItems.length === 0 ? (
                    <S.EmptySearchState>No matching workers</S.EmptySearchState>
                ) : (
                    sidebarItems.map((item) => {
                        if (item.kind === 'automation') {
                            const { worker } = item;
                            const selected =
                                store.selectedAutomationWorkerId === worker.id &&
                                store.isAutomationWorkerSelected;
                            const ruleLabel = `${worker.ruleCount} rule${worker.ruleCount === 1 ? '' : 's'}`;
                            const orderNumber = getAutomationWorkerOrderNumber(worker.id);

                            return (
                                <S.WorkerButton
                                    key={`automation:${worker.id}`}
                                    type="button"
                                    selected={selected}
                                    aria-current={selected ? 'true' : undefined}
                                    onClick={() => store.setSelectedAutomationWorker(worker.id)}
                                >
                                    <S.WorkerIcon selected={selected}>
                                        <Icon path={worker.icon} size={0.85} aria-hidden={true} />
                                    </S.WorkerIcon>
                                    {orderNumber && <S.WorkerOrderNumber>{orderNumber}</S.WorkerOrderNumber>}
                                    <S.WorkerBody>
                                        <S.WorkerName selected={selected}>{worker.name}</S.WorkerName>
                                        <S.WorkerSubtitle>{worker.desc}</S.WorkerSubtitle>
                                        <S.WorkerMeta>
                                            <S.RuleCountDot active={worker.active} />
                                            <S.MetaLabel>{ruleLabel}</S.MetaLabel>
                                        </S.WorkerMeta>
                                    </S.WorkerBody>
                                </S.WorkerButton>
                            );
                        }

                        const { workflowStatus } = item.status;
                        const selected =
                            store.selectedWorkerType === 'status' &&
                            store.selectedStatus === workflowStatus;
                        const configured = store.hasConfig(workflowStatus);
                        const label = getWorkflowStatusLabel(workflowStatus);
                        const description = getWorkflowStatusDescription(workflowStatus);
                        const orderNumber = getWorkflowStatusOrderNumber(workflowStatus);

                        return (
                            <S.StatusWorkerButton
                                key={`status:${workflowStatus}`}
                                type="button"
                                selected={selected}
                                aria-current={selected ? 'true' : undefined}
                                onClick={() => store.setSelectedStatus(workflowStatus)}
                            >
                                <S.WorkerIcon selected={selected}>
                                    <Icon
                                        path={getWorkflowStatusIcon(workflowStatus)}
                                        size={0.85}
                                        aria-hidden={true}
                                    />
                                </S.WorkerIcon>
                                {orderNumber && <S.WorkerOrderNumber>{orderNumber}</S.WorkerOrderNumber>}
                                <S.WorkerBody>
                                    <S.WorkerName selected={selected}>{label}</S.WorkerName>
                                    {description ? (
                                        <S.WorkerSubtitle>{description}</S.WorkerSubtitle>
                                    ) : null}
                                </S.WorkerBody>
                                <S.StatusDot
                                    configured={configured}
                                    title={configured ? 'Configured' : 'Not set'}
                                />
                            </S.StatusWorkerButton>
                        );
                    })
                )}
            </S.WorkerList>
        </S.Sidebar>
    );
});
